import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Container, Row, Col, Card, Button, Badge, Modal, Image, Tabs, Tab, Table, Form, Alert } from 'react-bootstrap';
import { fetchPedidosLimeira, fetchComprasSolicitacoes, fetchPedidoLimeiraDetalhe } from '../../services/api';
import Loading from '../../components/Loading/Loading';

const URGENCIA_CONFIG = {
    baixa: { label: 'Baixa', color: 'secondary', icon: '🟢' },
    normal: { label: 'Normal', color: 'info', icon: '🔵' },
    alta: { label: 'Alta', color: 'warning', icon: '🟡' },
    urgente: { label: 'Urgente', color: 'danger', icon: '🔴' }
};

const STATUS_PEDIDO_CONFIG = {
    aberto: { label: 'Aberto', color: 'warning', icon: '📦' },
    enviado: { label: 'Enviado', color: 'primary', icon: '🚚' },
    recebido: { label: 'Recebido', color: 'success', icon: '✅' },
    cancelado: { label: 'Cancelado', color: 'danger', icon: '❌' }
};

const STATUS_SOLICITACAO_CONFIG = {
    solicitado: { label: 'Solicitado', color: 'warning', icon: '⏳' },
    aprovado: { label: 'Aprovado', color: 'success', icon: '✅' },
    em_cotacao: { label: 'Em Cotação', color: 'info', icon: '💰' },
    aguardando_entrega: { label: 'Aguardando Entrega', color: 'primary', icon: '🚚' },
    recebido: { label: 'Recebido', color: 'success', icon: '📦' },
    cancelado: { label: 'Cancelado', color: 'danger', icon: '❌' }
};

const LimeiraHistorico = () => {
    const [activeTab, setActiveTab] = useState('pedidos');
    const [filtroStatusPedido, setFiltroStatusPedido] = useState('');
    const [filtroStatusSolicitacao, setFiltroStatusSolicitacao] = useState('');

    // Modal para detalhes do pedido
    const [showDetalheModal, setShowDetalheModal] = useState(false);
    const [pedidoSelecionado, setPedidoSelecionado] = useState(null);

    // Query para pedidos
    const { data: pedidosData, isLoading: isLoadingPedidos, refetch: refetchPedidos } = useQuery({
        queryKey: ['pedidos-limeira-historico', filtroStatusPedido],
        queryFn: () => fetchPedidosLimeira({ status: filtroStatusPedido }),
        refetchInterval: 60000
    });

    // Query para solicitações
    const { data: solicitacoesData, isLoading: isLoadingSolicitacoes, refetch: refetchSolicitacoes } = useQuery({
        queryKey: ['solicitacoes-historico', filtroStatusSolicitacao],
        queryFn: () => fetchComprasSolicitacoes({ status: filtroStatusSolicitacao }),
        refetchInterval: 60000
    });

    // Query para detalhes do pedido selecionado
    const { data: detalheData, isLoading: isLoadingDetalhe } = useQuery({
        queryKey: ['pedido-detalhe', pedidoSelecionado?.id],
        queryFn: () => fetchPedidoLimeiraDetalhe(pedidoSelecionado.id),
        enabled: !!pedidoSelecionado?.id
    });

    const pedidos = pedidosData?.data || [];
    const solicitacoes = solicitacoesData?.data || [];
    const statsPedidos = pedidosData?.stats || {};
    const statsSolicitacoes = solicitacoesData?.stats || {};

    const formatCurrency = (value) => {
        if (!value && value !== 0) return 'R$ -';
        return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const handleVerDetalhe = (pedido) => {
        setPedidoSelecionado(pedido);
        setShowDetalheModal(true);
    };

    const handleRefresh = () => {
        if (activeTab === 'pedidos') {
            refetchPedidos();
        } else {
            refetchSolicitacoes();
        }
    };

    // Renderização das estatísticas de pedidos
    const renderStatsPedidos = () => (
        <Row className="mb-4 g-3">
            <Col md={3}>
                <Card className="border-warning border-2 h-100">
                    <Card.Body className="d-flex align-items-center">
                        <div className="rounded-circle bg-warning text-white p-3 me-3" style={{ fontSize: '1.5rem' }}>📦</div>
                        <div>
                            <h5 className="mb-0">{statsPedidos.aberto || 0}</h5>
                            <small className="text-muted">Abertos</small>
                        </div>
                    </Card.Body>
                </Card>
            </Col>
            <Col md={3}>
                <Card className="border-primary border-2 h-100">
                    <Card.Body className="d-flex align-items-center">
                        <div className="rounded-circle bg-primary text-white p-3 me-3" style={{ fontSize: '1.5rem' }}>🚚</div>
                        <div>
                            <h5 className="mb-0">{statsPedidos.enviado || 0}</h5>
                            <small className="text-muted">Enviados</small>
                        </div>
                    </Card.Body>
                </Card>
            </Col>
            <Col md={3}>
                <Card className="border-success border-2 h-100">
                    <Card.Body className="d-flex align-items-center">
                        <div className="rounded-circle bg-success text-white p-3 me-3" style={{ fontSize: '1.5rem' }}>✅</div>
                        <div>
                            <h5 className="mb-0">{statsPedidos.recebido || 0}</h5>
                            <small className="text-muted">Recebidos</small>
                        </div>
                    </Card.Body>
                </Card>
            </Col>
            <Col md={3}>
                <Card className="border-info border-2 h-100">
                    <Card.Body className="d-flex align-items-center">
                        <div className="rounded-circle bg-info text-white p-3 me-3" style={{ fontSize: '1.5rem' }}>💰</div>
                        <div>
                            <h5 className="mb-0">{formatCurrency(statsPedidos.valor_total || 0)}</h5>
                            <small className="text-muted">Valor Total</small>
                        </div>
                    </Card.Body>
                </Card>
            </Col>
        </Row>
    );

    // Renderização das estatísticas de solicitações
    const renderStatsSolicitacoes = () => (
        <Row className="mb-4 g-3">
            <Col md={2}>
                <Card className="border-warning border-2 h-100">
                    <Card.Body className="text-center p-2">
                        <h5 className="mb-0">{statsSolicitacoes.solicitado || 0}</h5>
                        <small className="text-muted">Solicitados</small>
                    </Card.Body>
                </Card>
            </Col>
            <Col md={2}>
                <Card className="border-success border-2 h-100">
                    <Card.Body className="text-center p-2">
                        <h5 className="mb-0">{statsSolicitacoes.aprovado || 0}</h5>
                        <small className="text-muted">Aprovados</small>
                    </Card.Body>
                </Card>
            </Col>
            <Col md={2}>
                <Card className="border-info border-2 h-100">
                    <Card.Body className="text-center p-2">
                        <h5 className="mb-0">{statsSolicitacoes.em_cotacao || 0}</h5>
                        <small className="text-muted">Em Cotação</small>
                    </Card.Body>
                </Card>
            </Col>
            <Col md={2}>
                <Card className="border-primary border-2 h-100">
                    <Card.Body className="text-center p-2">
                        <h5 className="mb-0">{statsSolicitacoes.aguardando_entrega || 0}</h5>
                        <small className="text-muted">Ag. Entrega</small>
                    </Card.Body>
                </Card>
            </Col>
            <Col md={2}>
                <Card className="border-success border-2 h-100">
                    <Card.Body className="text-center p-2">
                        <h5 className="mb-0">{statsSolicitacoes.recebido || 0}</h5>
                        <small className="text-muted">Recebidos</small>
                    </Card.Body>
                </Card>
            </Col>
            <Col md={2}>
                <Card className="border-danger border-2 h-100">
                    <Card.Body className="text-center p-2">
                        <h5 className="mb-0">{statsSolicitacoes.urgentes || 0}</h5>
                        <small className="text-muted">Urgentes</small>
                    </Card.Body>
                </Card>
            </Col>
        </Row>
    );

    // Renderização da tabela de pedidos
    const renderTabelaPedidos = () => (
        <Card className="shadow">
            <Card.Header className="bg-primary text-white d-flex justify-content-between align-items-center">
                <span>📦 Histórico de Pedidos</span>
                <div className="d-flex gap-2 align-items-center">
                    <Form.Select
                        size="sm"
                        style={{ width: '150px' }}
                        value={filtroStatusPedido}
                        onChange={(e) => setFiltroStatusPedido(e.target.value)}
                    >
                        <option value="">Todos Status</option>
                        <option value="aberto">Aberto</option>
                        <option value="enviado">Enviado</option>
                        <option value="recebido">Recebido</option>
                        <option value="cancelado">Cancelado</option>
                    </Form.Select>
                    <Badge bg="light" text="dark">{pedidos.length}</Badge>
                </div>
            </Card.Header>
            <Card.Body style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                {pedidos.length === 0 ? (
                    <div className="text-center text-muted py-5">
                        <div style={{ fontSize: '4rem' }}>📭</div>
                        <h5 className="mt-3">Nenhum pedido encontrado</h5>
                        <p className="text-muted">Não há pedidos registrados com esse filtro.</p>
                    </div>
                ) : (
                    <Table responsive hover className="align-middle">
                        <thead className="table-light">
                            <tr>
                                <th>Nº Pedido</th>
                                <th>Data Criação</th>
                                <th>Itens</th>
                                <th>Valor Total</th>
                                <th>Status</th>
                                <th>Criado Por</th>
                                <th className="text-center">Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            {pedidos.map(pedido => {
                                const statusConfig = STATUS_PEDIDO_CONFIG[pedido.status] || STATUS_PEDIDO_CONFIG.aberto;
                                return (
                                    <tr key={pedido.id}>
                                        <td>
                                            <strong className="text-primary">{pedido.numero_pedido}</strong>
                                        </td>
                                        <td>{formatDate(pedido.data_criacao)}</td>
                                        <td>
                                            <Badge bg="dark">{pedido.total_itens || 0}</Badge>
                                        </td>
                                        <td className="text-success fw-bold">
                                            {formatCurrency(pedido.valor_total)}
                                        </td>
                                        <td>
                                            <Badge bg={statusConfig.color}>
                                                {statusConfig.icon} {statusConfig.label}
                                            </Badge>
                                        </td>
                                        <td>{pedido.usuario_criador_nome || '-'}</td>
                                        <td className="text-center">
                                            <Button
                                                variant="outline-primary"
                                                size="sm"
                                                onClick={() => handleVerDetalhe(pedido)}
                                            >
                                                👁️ Ver Itens
                                            </Button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </Table>
                )}
            </Card.Body>
        </Card>
    );

    // Renderização da tabela de solicitações
    const renderTabelaSolicitacoes = () => (
        <Card className="shadow">
            <Card.Header className="bg-success text-white d-flex justify-content-between align-items-center">
                <span>📋 Histórico de Solicitações</span>
                <div className="d-flex gap-2 align-items-center">
                    <Form.Select
                        size="sm"
                        style={{ width: '180px' }}
                        value={filtroStatusSolicitacao}
                        onChange={(e) => setFiltroStatusSolicitacao(e.target.value)}
                    >
                        <option value="">Todos Status</option>
                        <option value="solicitado">Solicitado</option>
                        <option value="aprovado">Aprovado</option>
                        <option value="em_cotacao">Em Cotação</option>
                        <option value="aguardando_entrega">Aguardando Entrega</option>
                        <option value="recebido">Recebido</option>
                        <option value="cancelado">Cancelado</option>
                    </Form.Select>
                    <Badge bg="light" text="dark">{solicitacoes.length}</Badge>
                </div>
            </Card.Header>
            <Card.Body style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                {solicitacoes.length === 0 ? (
                    <div className="text-center text-muted py-5">
                        <div style={{ fontSize: '4rem' }}>📭</div>
                        <h5 className="mt-3">Nenhuma solicitação encontrada</h5>
                        <p className="text-muted">Não há solicitações registradas com esse filtro.</p>
                    </div>
                ) : (
                    <Table responsive hover className="align-middle">
                        <thead className="table-light">
                            <tr>
                                <th style={{ width: '60px' }}>Imagem</th>
                                <th>Código</th>
                                <th>Produto</th>
                                <th>Fornecedor</th>
                                <th>Qtd</th>
                                <th>Valor</th>
                                <th>Origem</th>
                                <th>Urgência</th>
                                <th>Status</th>
                                <th>Data</th>
                            </tr>
                        </thead>
                        <tbody>
                            {solicitacoes.map(sol => {
                                const statusConfig = STATUS_SOLICITACAO_CONFIG[sol.status] || STATUS_SOLICITACAO_CONFIG.solicitado;
                                const urgenciaConfig = URGENCIA_CONFIG[sol.urgencia] || URGENCIA_CONFIG.normal;

                                return (
                                    <tr key={sol.id}>
                                        <td>
                                            {sol.produto_imagem ? (
                                                <Image
                                                    src={sol.produto_imagem}
                                                    style={{ width: '50px', height: '50px', objectFit: 'cover' }}
                                                    rounded
                                                />
                                            ) : (
                                                <div
                                                    style={{
                                                        width: '50px',
                                                        height: '50px',
                                                        background: '#f0f0f0',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        borderRadius: '4px'
                                                    }}
                                                >
                                                    📦
                                                </div>
                                            )}
                                        </td>
                                        <td>
                                            <small className="text-muted">#{sol.produto_codigo}</small>
                                        </td>
                                        <td>
                                            <div className="text-truncate" style={{ maxWidth: '200px' }} title={sol.produto_descricao}>
                                                {sol.produto_descricao}
                                            </div>
                                        </td>
                                        <td>{sol.fornecedor || '-'}</td>
                                        <td>
                                            <Badge bg="dark">{sol.quantidade_solicitada}</Badge>
                                        </td>
                                        <td className="text-success fw-bold">
                                            {formatCurrency(sol.valor_total)}
                                        </td>
                                        <td>
                                            <Badge
                                                bg={
                                                    sol.origem === 'Loja Centro' ? 'warning' :
                                                        sol.origem === 'Loja Caxias' ? 'primary' : 'secondary'
                                                }
                                                className={sol.origem === 'Loja Centro' ? 'text-dark' : ''}
                                                style={{
                                                    backgroundColor: (!sol.origem || sol.origem === 'E-commerce') ? '#6f42c1' : undefined
                                                }}
                                            >
                                                {sol.origem || 'E-commerce'}
                                            </Badge>
                                        </td>
                                        <td>
                                            <Badge bg={urgenciaConfig.color}>
                                                {urgenciaConfig.icon} {urgenciaConfig.label}
                                            </Badge>
                                        </td>
                                        <td>
                                            <Badge bg={statusConfig.color}>
                                                {statusConfig.icon} {statusConfig.label}
                                            </Badge>
                                        </td>
                                        <td>
                                            <small>{formatDate(sol.data_solicitacao)}</small>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </Table>
                )}
            </Card.Body>
        </Card>
    );

    // Modal de detalhes do pedido
    const itensPedido = detalheData?.data?.itens || [];

    return (
        <Container fluid className="p-4">
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h1 className="h3 mb-0 text-gray-800">📚 Histórico Limeira</h1>
                    <p className="mb-0 text-muted">Visualize o histórico de pedidos e solicitações</p>
                </div>
                <Button variant="outline-secondary" onClick={handleRefresh}>
                    🔄 Atualizar
                </Button>
            </div>

            {/* Tabs */}
            <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mb-4">
                <Tab eventKey="pedidos" title="📦 Histórico de Pedidos">
                    {isLoadingPedidos && <Loading />}
                    {!isLoadingPedidos && (
                        <>
                            {renderStatsPedidos()}
                            {renderTabelaPedidos()}
                        </>
                    )}
                </Tab>
                <Tab eventKey="solicitacoes" title="📋 Histórico de Solicitações">
                    {isLoadingSolicitacoes && <Loading />}
                    {!isLoadingSolicitacoes && (
                        <>
                            {renderStatsSolicitacoes()}
                            {renderTabelaSolicitacoes()}
                        </>
                    )}
                </Tab>
            </Tabs>

            {/* Modal de Detalhes do Pedido */}
            <Modal show={showDetalheModal} onHide={() => setShowDetalheModal(false)} size="lg" centered>
                <Modal.Header closeButton className="bg-primary text-white">
                    <Modal.Title>
                        📦 Pedido {pedidoSelecionado?.numero_pedido}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {isLoadingDetalhe ? (
                        <Loading />
                    ) : (
                        <>
                            {/* Info do Pedido */}
                            <Row className="mb-4">
                                <Col md={4}>
                                    <small className="text-muted d-block">Data Criação</small>
                                    <strong>{formatDate(pedidoSelecionado?.data_criacao)}</strong>
                                </Col>
                                <Col md={4}>
                                    <small className="text-muted d-block">Valor Total</small>
                                    <strong className="text-success">{formatCurrency(pedidoSelecionado?.valor_total)}</strong>
                                </Col>
                                <Col md={4}>
                                    <small className="text-muted d-block">Status</small>
                                    <Badge bg={STATUS_PEDIDO_CONFIG[pedidoSelecionado?.status]?.color || 'secondary'}>
                                        {STATUS_PEDIDO_CONFIG[pedidoSelecionado?.status]?.label || pedidoSelecionado?.status}
                                    </Badge>
                                </Col>
                            </Row>

                            {pedidoSelecionado?.observacoes_gerais && (
                                <Alert variant="info" className="mb-4">
                                    <strong>Observações:</strong> {pedidoSelecionado.observacoes_gerais}
                                </Alert>
                            )}

                            {/* Lista de Itens */}
                            <h6 className="mb-3">Itens do Pedido ({itensPedido.length})</h6>
                            {itensPedido.length === 0 ? (
                                <p className="text-muted">Nenhum item encontrado.</p>
                            ) : (
                                <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                    <Table responsive hover size="sm" className="align-middle mb-0">
                                        <thead className="table-light">
                                            <tr>
                                                <th style={{ width: '60px' }}>Img</th>
                                                <th>Código</th>
                                                <th>Descrição</th>
                                                <th className="text-center">Qtd</th>
                                                <th className="text-end">Valor</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {itensPedido.map(item => (
                                                <tr key={item.id}>
                                                    <td>
                                                        {item.produto_imagem ? (
                                                            <Image
                                                                src={item.produto_imagem}
                                                                style={{ width: '50px', height: '50px', objectFit: 'cover' }}
                                                                rounded
                                                            />
                                                        ) : (
                                                            <div
                                                                style={{
                                                                    width: '50px',
                                                                    height: '50px',
                                                                    background: '#f0f0f0',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    borderRadius: '6px',
                                                                    fontSize: '1.25rem'
                                                                }}
                                                            >
                                                                📦
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td>
                                                        <small className="text-muted">#{item.produto_codigo}</small>
                                                    </td>
                                                    <td>
                                                        <div className="text-truncate" style={{ maxWidth: '250px' }} title={item.produto_descricao}>
                                                            {item.produto_descricao}
                                                        </div>
                                                    </td>
                                                    <td className="text-center">
                                                        <Badge bg="info">Qtd: {item.quantidade_solicitada}</Badge>
                                                    </td>
                                                    <td className="text-end text-success fw-bold">
                                                        {formatCurrency(item.valor_total)}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            )}
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDetalheModal(false)}>
                        Fechar
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default LimeiraHistorico;
